import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { api } from "../lib/api";
import { logger } from "../lib/logger";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, DollarSign, Users, Briefcase, Package, BarChart3, ShieldCheck } from "lucide-react";

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-2xl border border-[#1F2A40] bg-[#131C2E] p-5">
      <div className="flex items-center gap-2 text-charcoal/60 text-xs uppercase tracking-widest">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <p className="font-display text-3xl text-charcoal mt-2">{value}</p>
      {hint && <p className="text-xs text-charcoal/50 mt-1">{hint}</p>}
    </div>
  );
}

function ProductEditor({ product, onSaved, onClose }) {
  const isNew = !product;
  const [form, setForm] = useState(product || { label: "", description: "", price: 0, category: "hardware", active: true });
  const save = async () => {
    try {
      const payload = { ...form, price: Number(form.price) };
      if (isNew) await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${product.id}`, payload);
      toast.success(isNew ? "Product created" : "Product updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <DialogContent className="bg-[#131C2E] border-[#1F2A40] text-charcoal max-w-lg">
      <DialogHeader><DialogTitle className="font-display text-2xl">{isNew ? "New product" : "Edit product"}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Label</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="prod-label" className="rounded-xl h-10 mt-1" /></div>
        <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="prod-desc" rows={2} className="rounded-xl mt-1" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Price ($)</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="prod-price" className="rounded-xl h-10 mt-1" /></div>
          <div><Label>Category</Label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="prod-cat"
              className="w-full h-10 rounded-xl bg-[#0B1220] border border-[#1F2A40] px-3 mt-1 text-charcoal">
              {["tv_mount", "tv", "hardware", "wallpaper", "painting", "other"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="prod-active" /><span className="text-sm">Active (shown to customers)</span></div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} className="rounded-full border-[#1F2A40]">Cancel</Button>
        <Button onClick={save} data-testid="prod-save" className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white">Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function ServiceEditor({ service, onSaved, onClose }) {
  const isNew = !service;
  const [form, setForm] = useState(service || { label: "", icon: "wrench", image: "", active: true });
  const save = async () => {
    try {
      if (isNew) await api.post("/admin/services", form);
      else await api.put(`/admin/services/${service.id}`, form);
      toast.success(isNew ? "Service created" : "Service updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <DialogContent className="bg-[#131C2E] border-[#1F2A40] text-charcoal max-w-lg">
      <DialogHeader><DialogTitle className="font-display text-2xl">{isNew ? "New service" : "Edit service"}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Label</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="svc-label" className="rounded-xl h-10 mt-1" /></div>
        <div><Label>Icon (lucide name)</Label><Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} data-testid="svc-icon" className="rounded-xl h-10 mt-1" placeholder="wrench" /></div>
        <div><Label>Image URL</Label><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="svc-image" className="rounded-xl h-10 mt-1" /></div>
        <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="svc-active" /><span className="text-sm">Active</span></div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} className="rounded-full border-[#1F2A40]">Cancel</Button>
        <Button onClick={save} data-testid="svc-save" className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white">Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [handymen, setHandymen] = useState([]);
  const [deletingHandyman, setDeletingHandyman] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [openProd, setOpenProd] = useState(false);
  const [openSvc, setOpenSvc] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [s, p, sv, h, b] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/products"),
        api.get("/admin/services"),
        api.get("/admin/handymen"),
        api.get("/admin/bookings?limit=100"),
      ]);
      setStats(s.data); setProducts(p.data); setServices(sv.data); setHandymen(h.data); setBookings(b.data);
    } catch (e) {
      logger.error("Failed to load admin data", e);
      Sentry.captureException(e);
      toast.error("Failed to load admin data");
    }
  }, []);

  useEffect(() => { if (user?.role === "admin") refresh(); }, [user, refresh]);

  if (loading) return <div className="p-12 text-center text-charcoal/60">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/admin/products/${id}`); toast.success("Deleted"); refresh(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteService = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try { await api.delete(`/admin/services/${id}`); toast.success("Deleted"); refresh(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setDiscount = async (uid, val) => {
    try { await api.post("/admin/handyman/discount", { user_id: uid, discount_verified: val }); toast.success("Discount updated"); refresh(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleHandyman = async (u) => {
    try {
      if (u.handyman_active) { await api.post(`/admin/handymen/${u.user_id}/deactivate`); }
      else { await api.post(`/admin/handymen/${u.user_id}/reactivate`); }
      toast.success("Updated"); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleWaiveFee = async (u) => {
    const endpoint = u.grandfathered ? "unwaive-fee" : "waive-fee";
    if (u.grandfathered && !window.confirm(`Remove the annual-fee waiver for ${u.email}? They'll be inactive until they pay.`)) return;
    if (!u.grandfathered && !window.confirm(`Waive the annual fee for ${u.email}? They'll be activated permanently (no expiry).`)) return;
    try {
      await api.post(`/admin/handymen/${u.user_id}/${endpoint}`);
      toast.success(u.grandfathered ? `Waiver removed for ${u.email}` : `Annual fee waived for ${u.email}`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteHandyman = async (u) => {
    if (deletingHandyman) return; // guard against double-invoke
    setDeletingHandyman(u.user_id);
    try {
      const r = await api.delete(`/admin/handymen/${u.user_id}`);
      const d = r.data.deleted || {};
      toast.success(
        `Deleted ${u.email} — ${d.handyman_profile || 0} profile, ${d.reviews_received || 0} reviews, ${d.messages || 0} messages, ${d.bookings_tagged || 0} bookings preserved for history`,
        { id: `delete-hm-${u.user_id}` },
      );
      refresh();
    } catch (e) {
      Sentry.captureException(e);
      toast.error(e.response?.data?.detail || "Failed to delete handyman", { id: `delete-hm-err-${u.user_id}` });
    } finally {
      setDeletingHandyman(null);
    }
  };

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-terracotta/15 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-terracotta" /></div>
          <h1 className="font-display text-4xl text-charcoal">Admin</h1>
        </div>
        <p className="text-charcoal/60">Manage products, services, handymen, bookings and revenue.</p>

        <Tabs defaultValue="stats" className="mt-8">
          <TabsList className="bg-[#131C2E] border border-[#1F2A40] rounded-full p-1" data-testid="admin-tabs">
            <TabsTrigger value="stats" className="rounded-full" data-testid="tab-stats"><BarChart3 className="w-4 h-4 mr-1" /> Overview</TabsTrigger>
            <TabsTrigger value="products" className="rounded-full" data-testid="tab-products"><Package className="w-4 h-4 mr-1" /> Products</TabsTrigger>
            <TabsTrigger value="services" className="rounded-full" data-testid="tab-services"><Briefcase className="w-4 h-4 mr-1" /> Services</TabsTrigger>
            <TabsTrigger value="handymen" className="rounded-full" data-testid="tab-handymen"><Users className="w-4 h-4 mr-1" /> Handymen</TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-full" data-testid="tab-bookings">Bookings</TabsTrigger>
          </TabsList>

          <TabsContent value="stats" className="mt-6">
            {stats && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={DollarSign} label="Platform revenue" value={`$${stats.revenue.total_platform_revenue.toFixed(0)}`} hint={`$${stats.revenue.platform_fees_bookings.toFixed(0)} booking fees + $${stats.revenue.activation_revenue.toFixed(0)} activations`} />
                <StatCard icon={DollarSign} label="Gross bookings" value={`$${stats.revenue.gross_bookings.toFixed(0)}`} hint={`${stats.counts.bookings_paid} paid bookings`} />
                <StatCard icon={Users} label="Active handymen" value={`${stats.counts.handymen_active}`} hint={`${stats.counts.handymen_total} total · ${stats.counts.handymen_paused} paused · ${stats.counts.handymen_canceled} canceling`} />
                <StatCard icon={Users} label="Students" value={`${stats.counts.students}`} hint={`${stats.counts.bookings_total} total bookings`} />
              </div>
            )}
          </TabsContent>

          <TabsContent value="products" className="mt-6">
            <div className="flex justify-between items-center mb-3">
              <p className="text-charcoal/60 text-sm">{products.length} products</p>
              <Dialog open={openProd} onOpenChange={(o) => { setOpenProd(o); if (!o) setEditingProduct(null); }}>
                <DialogTrigger asChild>
                  <Button onClick={() => setEditingProduct(null)} data-testid="new-product-btn" className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white"><Plus className="w-4 h-4 mr-1" /> New product</Button>
                </DialogTrigger>
                <ProductEditor product={editingProduct} onSaved={() => { setOpenProd(false); refresh(); }} onClose={() => setOpenProd(false)} />
              </Dialog>
            </div>
            <div className="bg-[#131C2E] rounded-2xl border border-[#1F2A40] overflow-hidden">
              <Table>
                <TableHeader><TableRow className="border-[#1F2A40]"><TableHead>Label</TableHead><TableHead>Category</TableHead><TableHead>Price</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {products.map((p) => (
                    <TableRow key={p.id} className="border-[#1F2A40]" data-testid={`prod-row-${p.id}`}>
                      <TableCell className="text-charcoal">{p.label}<p className="text-xs text-charcoal/50 mt-0.5">{p.id}</p></TableCell>
                      <TableCell><Badge className="rounded-full bg-beige text-charcoal/80">{p.category}</Badge></TableCell>
                      <TableCell className="text-terracotta font-semibold">${Number(p.price).toFixed(2)}</TableCell>
                      <TableCell>{p.active !== false ? <Badge className="bg-sage/15 text-sage">Active</Badge> : <Badge className="bg-charcoal/20 text-charcoal/60">Hidden</Badge>}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => { setEditingProduct(p); setOpenProd(true); }} data-testid={`edit-prod-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteProduct(p.id)} data-testid={`del-prod-${p.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="services" className="mt-6">
            <div className="flex justify-between items-center mb-3">
              <p className="text-charcoal/60 text-sm">{services.length} services</p>
              <Dialog open={openSvc} onOpenChange={(o) => { setOpenSvc(o); if (!o) setEditingService(null); }}>
                <DialogTrigger asChild>
                  <Button onClick={() => setEditingService(null)} data-testid="new-service-btn" className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white"><Plus className="w-4 h-4 mr-1" /> New service</Button>
                </DialogTrigger>
                <ServiceEditor service={editingService} onSaved={() => { setOpenSvc(false); refresh(); }} onClose={() => setOpenSvc(false)} />
              </Dialog>
            </div>
            <div className="bg-[#131C2E] rounded-2xl border border-[#1F2A40] overflow-hidden">
              <Table>
                <TableHeader><TableRow className="border-[#1F2A40]"><TableHead>Label</TableHead><TableHead>Icon</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {services.map((s) => (
                    <TableRow key={s.id} className="border-[#1F2A40]" data-testid={`svc-row-${s.id}`}>
                      <TableCell className="text-charcoal">{s.label}<p className="text-xs text-charcoal/50 mt-0.5">{s.id}</p></TableCell>
                      <TableCell><Badge className="rounded-full bg-beige text-charcoal/80">{s.icon}</Badge></TableCell>
                      <TableCell>{s.active !== false ? <Badge className="bg-sage/15 text-sage">Active</Badge> : <Badge className="bg-charcoal/20 text-charcoal/60">Hidden</Badge>}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => { setEditingService(s); setOpenSvc(true); }} data-testid={`edit-svc-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteService(s.id)} data-testid={`del-svc-${s.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="handymen" className="mt-6">
            <p className="text-charcoal/60 text-sm mb-3">{handymen.length} handymen</p>
            <div className="bg-[#131C2E] rounded-2xl border border-[#1F2A40] overflow-hidden">
              <Table>
                <TableHeader><TableRow className="border-[#1F2A40]"><TableHead>Name</TableHead><TableHead>Campus</TableHead><TableHead>Rate</TableHead><TableHead>Rating</TableHead><TableHead>Status</TableHead><TableHead>Discount</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {handymen.map((h) => (
                    <TableRow key={h.user_id} className="border-[#1F2A40]" data-testid={`hm-row-${h.user_id}`}>
                      <TableCell className="text-charcoal">{h.name}<p className="text-xs text-charcoal/50 mt-0.5">{h.email}</p></TableCell>
                      <TableCell className="text-charcoal/70">{h.campus || "—"}</TableCell>
                      <TableCell className="text-charcoal/70">{h.hourly_rate ? `$${h.hourly_rate}/hr` : "—"}</TableCell>
                      <TableCell className="text-charcoal/70">{h.rating ? `${h.rating.toFixed(1)} (${h.review_count})` : "—"}</TableCell>
                      <TableCell>
                        {h.grandfathered ? <Badge className="bg-terracotta/15 text-terracotta" data-testid={`waived-badge-${h.user_id}`}>Fee waived</Badge> :
                         h.subscription_paused_at ? <Badge className="bg-cognac/15 text-cognac">Paused</Badge> :
                         h.subscription_cancel_at_period_end ? <Badge className="bg-cognac/15 text-cognac">Canceling</Badge> :
                         h.handyman_active ? <Badge className="bg-sage/15 text-sage">Active</Badge> :
                         <Badge className="bg-charcoal/20 text-charcoal/60">Inactive</Badge>}
                      </TableCell>
                      <TableCell><Switch checked={!!h.discount_verified} onCheckedChange={(v) => setDiscount(h.user_id, v)} data-testid={`disc-${h.user_id}`} /></TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => toggleWaiveFee(h)} data-testid={`waive-${h.user_id}`}
                            className={`text-xs ${h.grandfathered ? "text-cognac hover:bg-cognac/10" : "text-terracotta hover:bg-terracotta/10"}`}>
                            {h.grandfathered ? "Unwaive" : "Waive fee"}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => toggleHandyman(h)} data-testid={`toggle-${h.user_id}`} className="text-xs">
                            {h.handyman_active ? "Deactivate" : "Reactivate"}
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="ghost" data-testid={`delete-hm-${h.user_id}`} className="text-xs text-destructive hover:bg-destructive/10">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent data-testid={`delete-hm-dialog-${h.user_id}`}>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete {h.name}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Permanently deletes this Move-in Pro&apos;s account, profile, reviews and messages. Their past bookings will be preserved for financial audit but tagged as pro-deleted. This cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel data-testid={`delete-hm-cancel-${h.user_id}`}>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  data-testid={`delete-hm-confirm-${h.user_id}`}
                                  onClick={() => deleteHandyman(h)}
                                  disabled={deletingHandyman === h.user_id}
                                  className="bg-destructive hover:bg-destructive/90 text-white disabled:opacity-50"
                                >
                                  {deletingHandyman === h.user_id ? "Deleting…" : "Delete permanently"}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            <p className="text-charcoal/60 text-sm mb-3">Last {bookings.length} bookings</p>
            <div className="bg-[#131C2E] rounded-2xl border border-[#1F2A40] overflow-hidden">
              <Table>
                <TableHeader><TableRow className="border-[#1F2A40]"><TableHead>Student</TableHead><TableHead>Move-in Pro</TableHead><TableHead>Category</TableHead><TableHead>Amount</TableHead><TableHead>Fee</TableHead><TableHead>Status</TableHead><TableHead>Payout</TableHead></TableRow></TableHeader>
                <TableBody>
                  {bookings.slice(0, 100).map((b) => (
                    <TableRow key={b.booking_id} className="border-[#1F2A40]" data-testid={`bk-row-${b.booking_id}`}>
                      <TableCell className="text-charcoal/80 text-sm">{b.student_name}</TableCell>
                      <TableCell className="text-charcoal/80 text-sm">{b.handyman_name}</TableCell>
                      <TableCell className="text-charcoal/70 text-xs">{b.category}</TableCell>
                      <TableCell className="text-terracotta font-semibold">${Number(b.amount || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-sage text-sm">${Number(b.platform_fee_amount || 0).toFixed(2)}</TableCell>
                      <TableCell><Badge className="rounded-full bg-beige text-charcoal/80 text-xs">{b.status}</Badge></TableCell>
                      <TableCell><Badge className="rounded-full bg-beige text-charcoal/80 text-xs">{b.payout_status || "—"}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
